import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categoryComics, categoriesList } from '@/mocks/categoryData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [currentPage, setCurrentPage] = useState(1);

  const category = categoriesList.find((c) => c.slug === slug);
  const pageTitle = category ? category.name : '分类';

  const sortedComics = [...categoryComics].sort((a, b) => {
    if (sortBy === 'popular') return parseInt(b.views) - parseInt(a.views);
    if (sortBy === 'rating') return b.rating - a.rating;
    return 0;
  });

  const filteredComics = sortedComics.filter((comic) => comic.slug === slug);

  const pageSize = 8;
  const totalPages = Math.ceil(filteredComics.length / pageSize);
  const paginatedComics = filteredComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ccbcht.com/' },
      { '@type': 'ListItem', position: 2, name: `${pageTitle}漫画`, item: 'http://ccbcht.com/category/all' },
    ],
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${pageTitle}漫画 - 韩漫在线`,
    description: `韩漫在线${pageTitle}漫画大全，为您精选最热门的${pageTitle}韩国漫画，支持全集下拉式阅读。`,
    url: 'http://ccbcht.com/category/all',
    isPartOf: { '@type': 'WebSite', name: '韩漫在线', url: 'http://ccbcht.com/' },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="px-4 md:px-6 py-16 md:py-20">
            <div className="max-w-[1200px] mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-800">{pageTitle}漫画</span>
              </nav>

              <h1 className="text-3xl md:text-5xl font-bold text-foreground-900 font-heading">
                {pageTitle}<span className="text-primary-600">漫画</span>
              </h1>
              <p className="mt-3 text-sm text-foreground-600 max-w-lg">
                共收录 <strong>{category?.count.toLocaleString() || 1280}</strong> 部{pageTitle}类<strong>韩国漫画</strong>，支持<strong>下拉式阅读</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-10">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex flex-wrap gap-2">
                {categoriesList.map((cat) => (
                  <Link
                    key={cat.id}
                    to="/category/all"
                    className={`px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-2 bg-background-100 rounded-lg p-1">
                {[
                  { key: 'latest', label: '最新' },
                  { key: 'popular', label: '人气' },
                  { key: 'rating', label: '评分' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => { setSortBy(opt.key as typeof sortBy); setCurrentPage(1); }}
                    className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {paginatedComics.map((comic) => (
                <Link
                  key={comic.id}
                  to="/category/all"
                  className="group block cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={`${comic.title} - 韩漫在线${pageTitle}漫画`}
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold text-background-50 bg-accent-500">
                        {comic.status}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3 bg-background-950/70 backdrop-blur-sm rounded px-1.5 py-0.5 text-[10px] text-background-50 font-medium flex items-center gap-0.5">
                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                      {comic.rating}
                    </div>
                  </div>
                  <div className="mt-2.5">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate font-heading">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-primary-600 font-medium">{comic.latestChapter}</span>
                      <span className="text-xs text-foreground-500 flex items-center gap-0.5">
                        <i className="ri-eye-line text-[10px]"></i>
                        {comic.views}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                      page === currentPage
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}